import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { uniq } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';

interface Args {
	blockM: Block;
}

export default class BlockSettingsQuickCreateComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization as VisualizationAiSummary;
	}

	@action
	addAllMetrics() {
		const onConfirm = () => {
			const layer = this.infolayerService.loading('Syncing metrics from dashboard...');
			this.visualization.addBoardMetricsToBlock().then(
				() => {
					layer.ok('Metrics successfully added!');
					next(this, this.trackAfterAdded);
				},
				() => {
					layer.close();
				},
			);
		};

		this.popupService.openConfirmPopup({
			onConfirm,
			title: 'Are you sure you want to replace all metrics on this datablock?',
			text: '',
			confirmAction: 'Replace',
			cancelAction: 'Cancel',
		});
	}

	private trackAfterAdded() {
		const metrics: string[] = [],
			sourceTypes: string[] = [];
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			const metricName = ds.metricSetup.metric?.name;
			if (metricName) {
				metrics.push(metricName);
			}
			const sourceType = ds.metricSetup.metric?.dataSourceTypeKey;
			if (sourceType) {
				sourceTypes.push(sourceType);
			}
		});
		this.analyticsService.event(TrackingEvent.BLOCK_PROPERTIES_METRIC_SELECTED, {
			datasources: uniq(sourceTypes),
			metrics,
			multiple_metrics_AI: true,
		});
	}
	<template>
		<div class="row row--quick-create">
			<div>Quick-start tip:</div>
			<a {{on "click" this.addAllMetrics}}>
				<CommonUiIconComponent @name="Wizard" @size="sm" />
				Add all metrics from this dashboard
			</a>
		</div>
	</template>
}
